import { useCallback, useEffect, useRef } from 'react';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, Decoration } from '@codemirror/view';
import { Compartment } from '@codemirror/state';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { defaultHighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';
import { zetaLanguage } from '../lang/zeta-mode';
import type { ZetaError } from '../runtime/types';

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  errors: ZetaError[];
  onRun: () => void;
  theme: 'light' | 'dark';
}

function errorExtension(errors: ZetaError[]) {
  const marks = errors.map((err) => ({
    line: err.line > 0 ? err.line - 1 : 0,
    message: err.message,
  }));

  return EditorView.decorations.of((view) => {
    const widgets = [];
    for (const mark of marks) {
      const lineObj = view.state.doc.line(mark.line + 1);
      if (lineObj) {
        const deco = Decoration.mark({
          class: 'cm-error-underline',
          attributes: { title: mark.message },
        });
        widgets.push(deco.range(lineObj.from, lineObj.to));
      }
    }
    return Decoration.set(widgets);
  });
}

const themeCompartment = new Compartment();
const errorCompartment = new Compartment();

export default function Editor({ value, onChange, errors, onRun, theme }: EditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeRef = useRef(theme);

  const handleRun = useCallback(
    () => {
      onRun();
      return true;
    },
    [onRun]
  );

  useEffect(() => {
    if (!editorRef.current) return;

    const isDark = theme === 'dark';

    const extensions = [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      zetaLanguage,
      keymap.of([
        ...defaultKeymap,
        indentWithTab,
        { key: 'Mod-Enter', run: handleRun, preventDefault: true },
      ]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChange(update.state.doc.toString());
        }
      }),
      EditorView.theme({
        '&': {
          fontFamily: 'var(--font-mono)',
          fontSize: '14px',
          height: '100%',
        },
        '.cm-content': {
          fontFamily: 'var(--font-mono)',
          padding: '12px 0',
        },
        '.cm-line': {
          padding: '0 12px',
        },
        '.cm-gutters': {
          fontFamily: 'var(--font-mono)',
          background: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border-color)',
          color: 'var(--fg-muted)',
        },
        '.cm-activeLineGutter': {
          background: 'var(--bg-tertiary)',
          color: 'var(--fg-secondary)',
        },
        '.cm-error-underline': {
          textDecoration: 'underline wavy var(--accent-error)',
        },
      }),
      themeCompartment.of(isDark ? oneDark : syntaxHighlighting(defaultHighlightStyle)),
      errorCompartment.of(errorExtension(errors)),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;
    themeRef.current = theme;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (themeRef.current === theme) return;
    themeRef.current = theme;
    view.dispatch({
      effects: themeCompartment.reconfigure(theme === 'dark' ? oneDark : syntaxHighlighting(defaultHighlightStyle)),
    });
  }, [theme]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: errorCompartment.reconfigure(errorExtension(errors)),
    });
  }, [errors]);

  return <div ref={editorRef} style={{ height: '100%', overflow: 'auto' }} />;
}
